import { useCallback, useEffect, useRef, useState } from 'react';

import type { SessionTerminalWsTokenResponse } from '../types';

export type TerminalConnectionState = 'idle' | 'connecting' | 'reconnecting' | 'attached' | 'detached' | 'error';

type AttachmentIntent = {
  sessionId: string;
  revision: number;
};

export type TerminalReconnectFailure = {
  event?: CloseEvent;
  requestId: string | null;
  tokenError?: unknown;
};

type PersistentTerminalConnectionOptions = {
  requestToken: (sessionId: string) => Promise<SessionTerminalWsTokenResponse>;
  socketUrl: (sessionId: string, token: string, requestId?: string | null) => string;
  fitAndSendResize: (socket: WebSocket) => void;
  onText: (text: string) => void;
  onBinary: (data: ArrayBuffer) => void;
  onAttached: () => void;
  onDetached: () => void;
  onProtocolError: (message: string, requestId: string | null) => void;
  onReconnectExhausted: (failure: TerminalReconnectFailure) => void;
  /**
   * Guards reconnects and completion of in-flight attaches. A parent that
   * fails closed during authorization refresh can synchronously turn this off
   * before React unmounts the terminal.
   */
  canAutomaticallyReconnect?: () => boolean;
};

// A terminal attach has to obtain a one-shot token and establish a WebSocket
// through the browser, proxy, backend, and executor. A few sub-second retries
// are not enough to cover a short executor reconnect or mobile radio change.
const MAX_RECONNECT_FAILURES = 5;
const RECONNECT_DELAYS_MS = [250, 500, 1_000, 2_000] as const;
const RECONNECT_PAUSE_RECHECK_MS = 1_000;

export function shouldReconnectTerminalSocket(
  readyState: number | null,
  connectingState: number,
  openState: number
) {
  return readyState !== connectingState && readyState !== openState;
}

export function isTransientTerminalProtocolError(message: string) {
  return message.trim().toLowerCase() === 'executor websocket disconnected';
}

/**
 * Keeps a browser attachment to a persistent executor tmux session alive.
 * The attachment is an explicit user intent. Foregrounding reconnects a
 * socket that actually closed, while Detach, Stop, changing a session, and
 * unmounting always cancel it.
 */
export function usePersistentTerminalConnection(options: PersistentTerminalConnectionOptions) {
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);
  const [connectionState, setConnectionState] = useState<TerminalConnectionState>('idle');
  const socketRef = useRef<WebSocket | null>(null);
  const connectionGenerationRef = useRef(0);
  const attachmentIntentRef = useRef<AttachmentIntent | null>(null);
  const attachmentRevisionRef = useRef(0);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<number | null>(null);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const isCurrentIntent = useCallback((sessionId: string, revision: number) => {
    const intent = attachmentIntentRef.current;
    return intent?.sessionId === sessionId && intent.revision === revision;
  }, []);

  const cancelAttachment = useCallback(() => {
    attachmentIntentRef.current = null;
    attachmentRevisionRef.current += 1;
    reconnectAttemptsRef.current = 0;
    clearReconnectTimer();
    connectionGenerationRef.current += 1;
    const socket = socketRef.current;
    socketRef.current = null;
    socket?.close();
  }, [clearReconnectTimer]);

  const openConnectionRef = useRef<(sessionId: string, revision: number) => void>(() => {});

  const scheduleReconnect = useCallback(
    (sessionId: string, revision: number, failure: TerminalReconnectFailure) => {
      if (!isCurrentIntent(sessionId, revision)) return;
      const failures = reconnectAttemptsRef.current + 1;
      reconnectAttemptsRef.current = failures;
      if (failures >= MAX_RECONNECT_FAILURES) {
        attachmentIntentRef.current = null;
        clearReconnectTimer();
        setConnectionState('error');
        optionsRef.current.onReconnectExhausted(failure);
        return;
      }

      setConnectionState('reconnecting');
      clearReconnectTimer();
      const retry = () => {
        reconnectTimerRef.current = null;
        if (!isCurrentIntent(sessionId, revision)) return;
        if (optionsRef.current.canAutomaticallyReconnect?.() === false) return;
        if (document.visibilityState === 'hidden' || navigator.onLine === false) {
          // Browsers do not always emit an online or visibility event for a
          // short network transition. Keep the attachment intent alive and
          // recheck it instead of stranding the UI in "reconnecting".
          reconnectTimerRef.current = window.setTimeout(retry, RECONNECT_PAUSE_RECHECK_MS);
          return;
        }
        openConnectionRef.current(sessionId, revision);
      };
      if (optionsRef.current.canAutomaticallyReconnect?.() === false) return;
      const delay = RECONNECT_DELAYS_MS[Math.min(failures - 1, RECONNECT_DELAYS_MS.length - 1)];
      reconnectTimerRef.current = window.setTimeout(retry, delay);
    },
    [clearReconnectTimer, isCurrentIntent]
  );

  const handleUnexpectedDisconnect = useCallback(
    (socket: WebSocket | null, sessionId: string, revision: number, failure: TerminalReconnectFailure) => {
      if (!isCurrentIntent(sessionId, revision)) return;
      if (socketRef.current === socket) socketRef.current = null;
      connectionGenerationRef.current += 1;
      socket?.close();
      scheduleReconnect(sessionId, revision, failure);
    },
    [isCurrentIntent, scheduleReconnect]
  );

  const openConnection = useCallback(
    async (sessionId: string, revision: number) => {
      if (!isCurrentIntent(sessionId, revision)) return;
      const generation = connectionGenerationRef.current + 1;
      connectionGenerationRef.current = generation;
      setConnectionState(reconnectAttemptsRef.current === 0 ? 'connecting' : 'reconnecting');
      let requestId: string | null = null;
      const isCurrent = () => connectionGenerationRef.current === generation && isCurrentIntent(sessionId, revision);
      try {
        const token = await optionsRef.current.requestToken(sessionId);
        if (!isCurrent()) return;
        // A foreground transition can invalidate authorization while the
        // token request is in flight. Do not turn any stale request, including
        // an explicit attach started just before pagehide, into a new socket
        // after its parent has failed closed.
        if (optionsRef.current.canAutomaticallyReconnect?.() === false) {
          return;
        }
        requestId = token.request_id ?? null;
        const socket = new WebSocket(optionsRef.current.socketUrl(sessionId, token.token, requestId));
        let disconnectHandled = false;
        const reportUnexpectedDisconnect = (failure: TerminalReconnectFailure) => {
          if (disconnectHandled || !isCurrent()) return;
          disconnectHandled = true;
          handleUnexpectedDisconnect(socket, sessionId, revision, failure);
        };
        socket.binaryType = 'arraybuffer';
        socket.onopen = () => {
          if (!isCurrent()) {
            socket.close();
            return;
          }
          optionsRef.current.fitAndSendResize(socket);
        };
        socket.onmessage = (event) => {
          if (!isCurrent()) return;
          if (typeof event.data === 'string') {
            try {
              const message = JSON.parse(event.data) as {
                type?: string;
                message?: string;
              };
              if (message.type === 'error') {
                const errorMessage = message.message ?? 'terminal error';
                if (isTransientTerminalProtocolError(errorMessage)) {
                  reportUnexpectedDisconnect({ requestId });
                  return;
                }
                attachmentIntentRef.current = null;
                clearReconnectTimer();
                if (socketRef.current === socket) socketRef.current = null;
                connectionGenerationRef.current += 1;
                socket.close();
                setConnectionState('error');
                optionsRef.current.onProtocolError(errorMessage, requestId);
              } else if (message.type === 'attached') {
                reconnectAttemptsRef.current = 0;
                setConnectionState('attached');
                optionsRef.current.onAttached();
              } else if (message.type === 'detached') {
                attachmentIntentRef.current = null;
                clearReconnectTimer();
                setConnectionState('detached');
                optionsRef.current.onDetached();
              }
            } catch {
              optionsRef.current.onText(event.data);
            }
            return;
          }
          if (event.data instanceof ArrayBuffer) {
            optionsRef.current.onBinary(event.data);
          } else if (event.data instanceof Blob) {
            void event.data.arrayBuffer().then((bytes) => {
              if (isCurrent()) optionsRef.current.onBinary(bytes);
            });
          }
        };
        socket.onerror = () => reportUnexpectedDisconnect({ requestId });
        socket.onclose = (event) => reportUnexpectedDisconnect({ event, requestId });
        socketRef.current = socket;
      } catch (tokenError) {
        if (!isCurrent()) return;
        handleUnexpectedDisconnect(null, sessionId, revision, {
          requestId,
          tokenError
        });
      }
    },
    [clearReconnectTimer, handleUnexpectedDisconnect, isCurrentIntent]
  );
  useEffect(() => {
    openConnectionRef.current = (sessionId, revision) => {
      void openConnection(sessionId, revision);
    };
  }, [openConnection]);

  const connect = useCallback(
    async (sessionId: string) => {
      cancelAttachment();
      const revision = attachmentRevisionRef.current + 1;
      attachmentRevisionRef.current = revision;
      attachmentIntentRef.current = { sessionId, revision };
      reconnectAttemptsRef.current = 0;
      await openConnection(sessionId, revision);
    },
    [cancelAttachment, openConnection]
  );

  const disconnect = useCallback(() => {
    cancelAttachment();
    setConnectionState('detached');
  }, [cancelAttachment]);

  const stop = useCallback(() => {
    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'stop' }));
    }
    disconnect();
  }, [disconnect]);

  const send = useCallback((message: unknown) => {
    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(message));
    }
  }, []);

  const fitAndSendResize = useCallback(() => {
    const socket = socketRef.current;
    if (socket) optionsRef.current.fitAndSendResize(socket);
  }, []);

  useEffect(() => {
    const resumeAttachment = () => {
      const intent = attachmentIntentRef.current;
      const socket = socketRef.current;
      if (optionsRef.current.canAutomaticallyReconnect?.() === false) return;
      if (!intent || document.visibilityState === 'hidden' || navigator.onLine === false) return;
      if (socket?.readyState === WebSocket.OPEN) {
        // Keep a live client intact. Besides avoiding unnecessary one-shot
        // token churn, this preserves a tmux session selected with Ctrl-b s;
        // reattaching the control-plane UUID would switch the user back.
        optionsRef.current.fitAndSendResize(socket);
        return;
      }
      if (!shouldReconnectTerminalSocket(
        socket?.readyState ?? null,
        WebSocket.CONNECTING,
        WebSocket.OPEN
      )) return;
      clearReconnectTimer();
      openConnectionRef.current(intent.sessionId, intent.revision);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'hidden') resumeAttachment();
    };
    window.addEventListener('online', resumeAttachment);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('online', resumeAttachment);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      cancelAttachment();
    };
  }, [cancelAttachment, clearReconnectTimer]);

  return { connectionState, connect, disconnect, stop, send, fitAndSendResize };
}
